import { mapEdgeCategoryToCategory } from '@modules/common/utils';
import { TaxonomyActionType } from '@modules/taxonomy/store/taxonomyActionType';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { Box, Button, Divider, Stack, Tooltip } from '@mui/material';
import { Rc } from 'generated/graphql';
import { toTitleCase } from 'v2/utils/strings';
import { shallow } from 'zustand/shallow';
import { CircularLoader } from '../CircularLoader';
import { EditPromptModeOverlay } from './EditPromptModeOverlay';
import { EditReasonDescriptionPrompt } from './EditReasonDescriptionPrompt';
import {
  getSelectedReasonDefinitions,
  trackCreateNewReasonEvents,
} from './helpers';
import { useGetReasonNameForRCs } from './hooks';
import { ReviewReasonCategorySelect } from './ReviewReasonCategorySelect';
import { ReviewReasonName } from './ReviewReasonName';
import { SelectedReasonDefinitionList } from './SelectedReasonDefinitionList';
import { SuggestedReasonDefinitionList } from './SuggestedReasonDefinitionList';

export const ReviewReason = () => {
  const dispatch = useTaxonomyStore(({ dispatch }) => dispatch);
  const { reasonDefinitions, isEditPromptMode, reasonNameError } =
    useTaxonomyStore(
      ({ createReasonV3State: { reasonDefinitions, visual } }) => ({
        reasonDefinitions,
        isEditPromptMode: visual?.isEditPromptMode,
        reasonNameError: visual?.reasonNameError,
      }),
      shallow,
    );

  const [getReasonNameForRC, { isLoading: isRegeneratingReasonDetails }] =
    useGetReasonNameForRCs({
      onCompleted: ({ displayName, category }) => {
        dispatch({
          type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
          payload: {
            reason: {
              name: toTitleCase(displayName),
              category: mapEdgeCategoryToCategory(category),
            },
          },
        });
      },
    });

  const onRegenerateClick = () => {
    const reasonCandidates = reasonDefinitions?.map((definition): Rc => {
      if (definition.__typename === 'SearchedUnassignedRc') {
        return {
          ...definition,
          __typename: 'UnassignedRC',
        };
      }
      return definition;
    });

    try {
      trackCreateNewReasonEvents({
        eventName: 'Create New Reason - Regenerate CTA Clicked',
      });
    } catch {}

    getReasonNameForRC(getSelectedReasonDefinitions(reasonCandidates));
  };

  const renderContent = () => {
    return (
      <Box height="100%">
        <Box p={3} pt={reasonNameError ? 4 : undefined}>
          <Stack direction="row" spacing={4}>
            <ReviewReasonCategorySelect
              sxProps={{
                fieldset: {
                  border: isRegeneratingReasonDetails ? '2px solid' : undefined,
                  borderColor: (theme) =>
                    isRegeneratingReasonDetails
                      ? theme.palette.primary.main
                      : undefined,
                },
              }}
            />
            <Stack alignItems="start" direction="row" spacing={2.15}>
              <ReviewReasonName
                sxProps={{
                  fieldset: {
                    border: isRegeneratingReasonDetails
                      ? '2px solid'
                      : undefined,
                    borderColor: (theme) =>
                      isRegeneratingReasonDetails
                        ? theme.palette.primary.main
                        : undefined,
                  },
                }}
              />
              <Box pt={0.25}>
                {isRegeneratingReasonDetails ? (
                  <Box pt={0.5} pl={1}>
                    <CircularLoader size={26} />
                  </Box>
                ) : (
                  <Tooltip
                    arrow
                    title="Regenerate Reason name and category"
                    placement="top"
                  >
                    <Button onClick={onRegenerateClick}>Regenerate</Button>
                  </Tooltip>
                )}
              </Box>
            </Stack>
          </Stack>
        </Box>
        <Divider sx={{ opacity: 0.1 }} />
        <Stack
          direction="row"
          width="100%"
          height="calc(100% - 165px)"
          pt={1.25}
          px={1}
        >
          <Stack
            width="50%"
            height="100%"
            borderRight="1px solid"
            borderColor="divider"
          >
            <SelectedReasonDefinitionList />
          </Stack>
          <Stack width="50%" height="100%">
            <SuggestedReasonDefinitionList />
          </Stack>
        </Stack>
      </Box>
    );
  };

  return (
    <Box height="100%">
      <EditReasonDescriptionPrompt />
      {isEditPromptMode && <EditPromptModeOverlay />}
      {isEditPromptMode ? (
        <Box
          sx={{
            opacity: 0.2,
            filter: 'blur(3px)',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          {renderContent()}
        </Box>
      ) : (
        renderContent()
      )}
    </Box>
  );
};
